import { z } from "zod";
import { figureOrNothing, figureSchema, figureWireSchema } from "../figures/index.js";
import { decodeStrayEscapes } from "../text.js";
/**
 * A shell is a hand-built, tested check component. The model never writes code
 * for one — it emits a spec that parameterises a shell we already shipped.
 *
 * Everything a shell needs lives in its own file: the structural schema, the
 * rules JSON Schema can't express, the fragment that teaches a model when to
 * pick it, and a worked example. Adding a shell is one new file plus one
 * `registerShell` call, plus a renderer on the client keyed by the same id.
 */
/** Every spec carries these, whatever the shell. */
export const baseSpecShape = {
    prompt: z
        .string()
        .min(1)
        .max(120)
        .describe("The instruction, in language a 6-year-old can follow when read aloud."),
    right: z
        .string()
        .min(1)
        .max(120)
        .describe("Shown on success. Restates the idea, never just 'Correct!'"),
    wrong: z
        .string()
        .min(1)
        .max(120)
        .describe("Shown on a miss. Corrects gently and gives the answer away rather than gatekeeping."),
    /**
     * Optional, and on the BASE shape rather than on any one shell — so every
     * shell gets a picture at once, and a new shell cannot ship without the
     * option, in the same way `CheckFrame` means no shell can forget its retry.
     */
    figure: figureSchema.optional(),
};
const registry = new Map();
export function registerShell(plugin) {
    if (registry.has(plugin.id)) {
        throw new Error(`Shell "${plugin.id}" is already registered.`);
    }
    registry.set(plugin.id, plugin);
    return plugin;
}
export function getShell(id) {
    return registry.get(id);
}
/**
 * The kid-facing line for a shell, degrading to something true of every check
 * if the id is unknown — an older client meeting a newer shell should still be
 * able to offer the star, just less specifically.
 */
export function invitationFor(shellId) {
    return getShell(shellId ?? "")?.invitation ?? "A quick check on what you just read.";
}
export function allShells() {
    return [...registry.values()];
}
export function shellIds() {
    return [...registry.keys()];
}
/**
 * Trims over-long strings to the limits the shells declare, before validation.
 *
 * These limits are about fitting a button and being readable aloud — they are
 * presentation, not truth. A spec that teaches the right thing but writes a
 * 43-character label is correct work, and throwing it away costs a full model
 * round trip to regenerate something that was already fine. So it gets trimmed
 * at a word boundary and kept.
 *
 * Semantic rules are a different matter and are never softened this way: a pick
 * with two correct answers is wrong, not long.
 */
export function normaliseSpec(candidate) {
    if (typeof candidate !== "object" || candidate === null)
        return candidate;
    const type = candidate.type;
    if (typeof type !== "string")
        return candidate;
    const shell = getShell(type);
    if (!shell)
        return candidate;
    const limits = {
        type: "object",
        properties: {
            prompt: { maxLength: 120 },
            right: { maxLength: 120 },
            wrong: { maxLength: 120 },
            ...shell.properties,
        },
    };
    const clamped = clampTo(limits, candidate);
    /*
      The figure resolves here rather than in the shell schema, and a bad one is
      dropped rather than failing the spec. A check that fails validation costs
      the child their star and is worth a retry; a picture that fails costs them
      a picture, and every answer rendered without one until last week.
    */
    if ("figure" in clamped) {
        const figure = figureOrNothing(clamped["figure"]);
        if (figure)
            clamped["figure"] = figure;
        else
            delete clamped["figure"];
    }
    return clamped;
}
function clampTo(schema, value) {
    if (typeof value === "string") {
        /*
          Decode before trimming, not after. A four-character `emoji` slot holding
          the literal text of an escape sequence gets cut to `\u2…` — the label is
          then both wrong and unrecoverable, because the digits that said which
          character it was have been thrown away.
        */
        const text = decodeStrayEscapes(value);
        const max = schema["maxLength"];
        return typeof max === "number" ? trimTo(text, max) : text;
    }
    if (Array.isArray(value)) {
        const items = schema["items"];
        const trimmed = items ? value.map((entry) => clampTo(items, entry)) : value;
        const maxItems = schema["maxItems"];
        return typeof maxItems === "number" ? trimmed.slice(0, maxItems) : trimmed;
    }
    if (typeof value === "object" && value !== null) {
        const properties = schema["properties"];
        if (!properties)
            return value;
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [
            key,
            properties[key] ? clampTo(properties[key], entry) : entry,
        ]));
    }
    return value;
}
/** Cuts at a word boundary where possible, so a label never ends mid-word. */
function trimTo(text, max) {
    const clean = text.trim().replace(/\s+/g, " ");
    if (clean.length <= max)
        return clean;
    const cut = clean.slice(0, max - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
/**
 * The one function that decides whether a spec is allowed to reach a device.
 * Nothing in the service may render or persist a spec that hasn't been through
 * here. Unknown shell ids fail closed.
 */
export function validateSpec(candidate) {
    if (typeof candidate !== "object" || candidate === null) {
        return { ok: false, issues: ["Spec is not an object."] };
    }
    const type = candidate.type;
    if (typeof type !== "string") {
        return { ok: false, issues: ["Spec is missing a string `type`."] };
    }
    const shell = getShell(type);
    if (!shell) {
        return { ok: false, issues: [`Unknown shell "${type}". Registered: ${shellIds().join(", ")}.`] };
    }
    const parsed = shell.schema.safeParse(candidate);
    if (!parsed.success) {
        return {
            ok: false,
            issues: parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`),
        };
    }
    const semantic = shell.semanticIssues(parsed.data);
    if (semantic.length > 0)
        return { ok: false, issues: semantic };
    return { ok: true, spec: parsed.data };
}
/**
 * Walks a JSON Schema and closes every object: `additionalProperties: false`
 * and every declared property required. Structured-output modes across
 * providers demand this shape and reject `if`/`then`, which is why we constrain
 * a model to ONE shell at a time rather than handing it the composite contract.
 */
function strictify(node) {
    const out = { ...node };
    const properties = out["properties"];
    if (properties) {
        out["properties"] = Object.fromEntries(Object.entries(properties).map(([key, value]) => [key, strictify(value)]));
        out["required"] = Object.keys(properties);
        out["additionalProperties"] = false;
    }
    const items = out["items"];
    if (items)
        out["items"] = strictify(items);
    return out;
}
/**
 * Constraint keywords that structured-output modes reject. Providers compile
 * the schema into a grammar and only support a subset of JSON Schema —
 * `minItems` above 1, and every length or numeric bound, are out.
 *
 * They still matter, so rather than dropping them we move them into the
 * description where the model can read them as instructions. The real
 * enforcement was never here anyway: `validateSpec` is the gate, and a spec
 * that ignores these is rejected and regenerated.
 */
function describeConstraints(node) {
    const parts = [];
    const min = node["minItems"];
    const max = node["maxItems"];
    if (typeof min === "number" && typeof max === "number") {
        parts.push(min === max ? `Exactly ${min} items.` : `Between ${min} and ${max} items.`);
    }
    else if (typeof min === "number")
        parts.push(`At least ${min} items.`);
    else if (typeof max === "number")
        parts.push(`At most ${max} items.`);
    const maxLength = node["maxLength"];
    if (typeof maxLength === "number")
        parts.push(`At most ${maxLength} characters.`);
    return parts.join(" ");
}
const UNSUPPORTED = [
    "minItems",
    "maxItems",
    "minLength",
    "maxLength",
    "minimum",
    "maximum",
    "multipleOf",
    "pattern",
];
function forStructuredOutput(node) {
    const out = { ...node };
    const note = describeConstraints(out);
    for (const key of UNSUPPORTED)
        delete out[key];
    if (note) {
        const existing = typeof out["description"] === "string" ? `${out["description"]} ` : "";
        out["description"] = `${existing}${note}`.trim();
    }
    const properties = out["properties"];
    if (properties) {
        out["properties"] = Object.fromEntries(Object.entries(properties).map(([key, value]) => [key, forStructuredOutput(value)]));
    }
    const items = out["items"];
    if (items)
        out["items"] = forStructuredOutput(items);
    return out;
}
/**
 * The single-shell schema handed to a model as an output constraint. Optional
 * fields become required here on purpose: a model that must emit `correct` on
 * every option produces cleaner specs than one that may omit it, and the
 * validator downstream treats the field the same either way.
 */
export function strictSchemaFor(shell) {
    return forStructuredOutput(strictify({
        type: "object",
        properties: {
            type: { type: "string", enum: [shell.id] },
            prompt: {
                type: "string",
                description: "The instruction, in language a 6-year-old can follow when read aloud.",
            },
            ...shell.properties,
            right: { type: "string", description: "Shown on success. Restates the idea." },
            wrong: { type: "string", description: "Shown on a miss. Gives the answer away, gently." },
            figure: figureWireSchema(),
        },
    }));
}
/**
 * The published contract, assembled from whatever is registered. `schemas/
 * check-spec.json` is emitted from this so the file can never drift from the
 * code that enforces it (`npm run schema:emit -w @howcomet/shared`).
 */
export function buildCheckSpecJsonSchema() {
    const shells = allShells();
    return {
        $schema: "https://json-schema.org/draft/2020-12/schema",
        $id: "https://howcomet.com/schemas/check-spec.json",
        title: "Check spec",
        description: "The declarative payload a model emits to fill a hand-built check shell. The client NEVER executes model-generated code; it renders one of a fixed set of shells from this spec. Validate before it reaches a device. Generated from packages/shared/src/shells — edit there, not here.",
        type: "object",
        required: ["type", "prompt", "right", "wrong"],
        properties: {
            type: { enum: shells.map((s) => s.id) },
            prompt: {
                type: "string",
                maxLength: 120,
                description: "The instruction, in language a 6-year-old can follow when read aloud.",
            },
            right: {
                type: "string",
                maxLength: 120,
                description: "Shown on success. Restates the idea, never just 'Correct!'",
            },
            wrong: {
                type: "string",
                maxLength: 120,
                description: "Shown on a miss. Corrects gently and gives the answer away rather than gatekeeping.",
            },
            figure: {
                ...figureWireSchema(),
                description: "Optional. A hand-built picture the model parameterises but never draws. " +
                    "Omit it unless the picture does work the sentences cannot. Generated " +
                    "from packages/shared/src/figures.",
                required: ["kind", "caption"],
            },
        },
        allOf: shells.map((shell) => ({
            if: { properties: { type: { const: shell.id } } },
            then: { required: [...shell.required], properties: shell.properties },
        })),
        $comment: "Rules not expressible here are enforced in code by each shell's semanticIssues(). On validation failure, do not retry silently more than once — fall back to a cached check for the topic.",
        examples: shells.map((s) => s.example),
    };
}
//# sourceMappingURL=base.js.map